<template>
  <main class="adm-page">

    <!-- ── Header ──────────────────────────────────── -->
    <div class="adm-header">
      <div>
        <h1 class="adm-header__title">Événements</h1>
        <p class="adm-header__sub">{{ meta.total || 0 }} événements au total</p>
      </div>
      <div class="adm-header__actions">
        <button class="adm-btn adm-btn--terra" @click="openNewDialog"><Plus />Nouvel événement</button>
      </div>
    </div>

    <!-- ── KPIs ─────────────────────────────────────── -->
    <div class="adm-kpis">
      <div class="adm-kpi adm-kpi--forest"><div class="adm-kpi__icon"><Calendar /></div><span class="adm-kpi__val">{{ stats.total||0 }}</span><span class="adm-kpi__lbl">Total</span></div>
      <div class="adm-kpi adm-kpi--sage"><div class="adm-kpi__icon"><Globe /></div><span class="adm-kpi__val">{{ stats.published||0 }}</span><span class="adm-kpi__lbl">Publiés</span></div>
      <div class="adm-kpi adm-kpi--amber"><div class="adm-kpi__icon"><Clock /></div><span class="adm-kpi__val">{{ stats.upcoming||0 }}</span><span class="adm-kpi__lbl">À venir</span></div>
      <div class="adm-kpi adm-kpi--terra"><div class="adm-kpi__icon"><FileText /></div><span class="adm-kpi__val">{{ stats.draft||0 }}</span><span class="adm-kpi__lbl">Brouillons</span></div>
    </div>

    <!-- ── Filtres ──────────────────────────────────── -->
    <div class="adm-filters">
      <div class="adm-search" style="flex:1;min-width:200px"><Search /><input v-model="filters.search" @input="debounceSearch" placeholder="Titre, lieu, catégorie…" class="adm-input" /></div>
      <select v-model="filters.status" @change="loadEvents" class="adm-input adm-input--sm">
        <option value="">Tous statuts</option>
        <option value="draft">Brouillon</option>
        <option value="published">Publié</option>
        <option value="cancelled">Annulé</option>
        <option value="completed">Terminé</option>
      </select>
      <select v-model="filters.filter" @change="loadEvents" class="adm-input adm-input--sm">
        <option value="">Tous</option>
        <option value="upcoming">À venir</option>
        <option value="past">Passés</option>
      </select>
      <select v-model="filters.category" @change="loadEvents" class="adm-input adm-input--sm">
        <option value="">Toutes catégories</option>
        <option v-for="c in categories" :key="c" :value="c">{{ c }}</option>
      </select>
      <button class="adm-btn adm-btn--outline adm-btn--sm" @click="resetFilters"><X />Réinitialiser</button>
    </div>

    <!-- ── Table ────────────────────────────────────── -->
    <div class="adm-card">
      <div v-if="loading" class="adm-loading"><span class="adm-ring"></span></div>
      <div v-else>
        <div class="adm-table-wrap">
          <table class="adm-table">
            <thead>
              <tr>
                <th>Événement</th><th>Date</th><th>Lieu</th>
                <th>Inscrits</th><th>Dons</th><th>Catégorie</th><th>Statut</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!events.length"><td colspan="8" class="adm-table__empty">Aucun événement trouvé</td></tr>
              <tr v-for="ev in events" :key="ev.id">
                <td data-label="Événement">
                  <div style="display:flex;align-items:center;gap:.65rem">
                    <div class="adm-thumb">
                      <img v-if="ev.featured_image" :src="imageUrl+ev.featured_image" :alt="ev.title" /><CalendarDays v-else />
                    </div>
                    <div>
                      <div class="adm-cell-title">{{ ev.title }}</div>
                      <div class="adm-cell-sub" v-if="ev.is_featured">⭐ À la une</div>
                    </div>
                  </div>
                </td>
                <td data-label="Date" class="adm-cell-date">
                  <div>{{ fmtDate(ev.start_date) }}</div>
                  <div v-if="ev.end_date" style="font-size:.73rem;color:var(--sage)">→ {{ fmtDate(ev.end_date) }}</div>
                </td>
                <td data-label="Lieu">
                  <span class="adm-chip"><MapPin />{{ ev.is_online ? 'En ligne' : (ev.city||ev.location||'—') }}</span>
                </td>
                <td data-label="Inscrits">
                  <div style="display:flex;flex-direction:column;gap:.25rem">
                    <span class="adm-cell-num">{{ ev.registered_count || 0 }}<span v-if="ev.capacity"> / {{ ev.capacity }}</span></span>
                    <div v-if="ev.capacity" class="adm-progress-track" style="width:80px">
                      <div class="adm-progress-fill" :style="{width:Math.min(100,(ev.registered_count||0)/ev.capacity*100)+'%'}"></div>
                    </div>
                  </div>
                </td>
                <td data-label="Dons">
                  <span v-if="ev.donations_enabled" class="adm-badge adm-badge--green">{{ fmtAmount(ev.donation_collected) }}</span>
                  <span v-else class="adm-badge adm-badge--gray">—</span>
                </td>
                <td data-label="Catégorie"><span class="adm-chip">{{ ev.category }}</span></td>
                <td data-label="Statut">
                  <span class="adm-badge" :class="{
                    'adm-badge--green':  ev.status==='published',
                    'adm-badge--amber':  ev.status==='draft',
                    'adm-badge--red':    ev.status==='cancelled',
                    'adm-badge--forest': ev.status==='completed'
                  }">{{ statusLabel(ev.status) }}</span>
                </td>
                <td class="no-label">
                  <div class="adm-actions">
                    <button class="adm-btn adm-btn--outline adm-btn--xs" title="Inscriptions" @click="openRegistrations(ev)"><Users /></button>
                    <div class="adm-drop">
                      <button class="adm-btn adm-btn--outline adm-btn--xs" @click.stop="toggleMenu(ev.id)"><MoreVertical /></button>
                      <div v-if="activeMenu===ev.id" class="adm-drop__menu">
                        <button class="adm-drop__item" @click="editEvent(ev)"><Edit />Modifier</button>
                        <button class="adm-drop__item" @click="toggleStatus(ev)">
                          <Globe v-if="ev.status!=='published'" /><EyeOff v-else />
                          {{ ev.status==='published' ? 'Dépublier' : 'Publier' }}
                        </button>
                        <button class="adm-drop__item adm-drop__item--red" @click="confirmDelete(ev)"><Trash2 />Supprimer</button>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="meta.last_page > 1" class="adm-pagination">
          <p class="adm-pagination__info">{{ meta.total }} événements</p>
          <div class="adm-pagination__pages">
            <button class="adm-page-btn" :disabled="meta.current_page===1" @click="changePage(meta.current_page-1)"><ChevronLeft /></button>
            <button v-for="p in displayedPages" :key="p" class="adm-page-btn" :class="{'adm-page-btn--active':meta.current_page===p}" @click="changePage(p)">{{ p }}</button>
            <button class="adm-page-btn" :disabled="meta.current_page===meta.last_page" @click="changePage(meta.current_page+1)"><ChevronRight /></button>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ MODAL : Formulaire événement ══════════════ -->
    <Transition name="adm-modal">
      <div v-if="showDialog" class="adm-modal" @click.self="closeDialog">
        <div class="adm-modal__box adm-modal__box--xl">
          <div class="adm-modal__head">
            <h3 class="adm-modal__title">{{ editingEvent ? 'Modifier l\'événement' : 'Nouvel événement' }}</h3>
            <button class="adm-modal__close" @click="closeDialog"><X /></button>
          </div>
          <form @submit.prevent="submitForm" class="adm-modal__body">

            <div class="adm-field"><label class="adm-label">Titre <span>*</span></label><input v-model="form.title" required class="adm-input" placeholder="Ex : Tournoi de Football Stag Fondation 2026" /></div>

            <div class="adm-form-grid">
              <div class="adm-field"><label class="adm-label">Catégorie <span>*</span></label>
                <select v-model="form.category" class="adm-input">
                  <option value="Tournoi">Tournoi sportif</option>
                  <option value="Conférence">Conférence</option>
                  <option value="Formation">Formation</option>
                  <option value="Sondage">Sondage / Étude</option>
                  <option value="Gala">Gala / Cérémonie</option>
                  <option value="Sensibilisation">Sensibilisation</option>
                  <option value="Événement">Autre</option>
                </select>
              </div>
              <div class="adm-field"><label class="adm-label">Type</label>
                <select v-model="form.event_type" class="adm-input">
                  <option value="public">Public (ouvert à tous)</option>
                  <option value="membres">Membres uniquement</option>
                  <option value="payant">Payant</option>
                </select>
              </div>
              <div class="adm-field"><label class="adm-label">Statut</label>
                <select v-model="form.status" class="adm-input">
                  <option value="draft">Brouillon</option><option value="published">Publié</option>
                  <option value="cancelled">Annulé</option><option value="completed">Terminé</option>
                </select>
              </div>
            </div>

            <div class="adm-form-grid">
              <div class="adm-field"><label class="adm-label">Date de début <span>*</span></label><input v-model="form.start_date" type="datetime-local" required class="adm-input" /></div>
              <div class="adm-field"><label class="adm-label">Date de fin</label><input v-model="form.end_date" type="datetime-local" class="adm-input" /></div>
              <div class="adm-field"><label class="adm-label">Date limite d'inscription</label><input v-model="form.registration_deadline" type="datetime-local" class="adm-input" /></div>
              <div class="adm-field"><label class="adm-label">Capacité max (vide = illimité)</label><input v-model.number="form.capacity" type="number" min="1" class="adm-input" /></div>
            </div>

            <!-- Lieu -->
            <div class="adm-form-grid">
              <div class="adm-field"><label class="adm-label">Ville</label><input v-model="form.city" class="adm-input" placeholder="Douala" /></div>
              <div class="adm-field"><label class="adm-label">Adresse complète</label><input v-model="form.location" class="adm-input" placeholder="Stade, quartier, etc." /></div>
            </div>
            <div class="adm-field">
              <label class="adm-label" style="display:flex;align-items:center;gap:.5rem;cursor:pointer">
                <div class="adm-toggle" :class="{'adm-toggle--on':form.is_online}" @click="form.is_online=!form.is_online"><span class="adm-toggle__knob"></span></div>
                Événement en ligne
              </label>
              <input v-if="form.is_online" v-model="form.online_url" class="adm-input" placeholder="https://meet.google.com/…" style="margin-top:.5rem" />
            </div>

            <!-- Frais -->
            <div v-if="form.event_type==='payant'" class="adm-form-grid">
              <div class="adm-field"><label class="adm-label">Frais d'inscription</label><input v-model.number="form.registration_fee" type="number" min="0" class="adm-input" /></div>
              <div class="adm-field"><label class="adm-label">Devise</label>
                <select v-model="form.fee_currency" class="adm-input">
                  <option value="XAF">XAF (FCFA)</option><option value="EUR">EUR</option><option value="USD">USD</option>
                </select>
              </div>
            </div>

            <!-- Dons -->
            <div style="border-top:1px solid var(--ink-12);padding-top:1rem">
              <p class="adm-section-title">Collecte de dons</p>
              <div class="adm-field">
                <label class="adm-label" style="display:flex;align-items:center;gap:.5rem;cursor:pointer">
                  <div class="adm-toggle" :class="{'adm-toggle--on':form.donations_enabled}" @click="form.donations_enabled=!form.donations_enabled"><span class="adm-toggle__knob"></span></div>
                  Accepter des dons liés à cet événement
                </label>
              </div>
              <div v-if="form.donations_enabled" class="adm-field">
                <label class="adm-label">Objectif de collecte (EUR)</label>
                <input v-model.number="form.donation_goal" type="number" min="0" class="adm-input" placeholder="Ex: 500" />
              </div>
            </div>

            <!-- Médias -->
            <div class="adm-field">
              <label class="adm-label">Image principale</label>
              <input ref="imgInput" type="file" accept="image/*" @change="handleImg" style="display:none" />
              <div v-if="form.featured_image_preview || form.featured_image" class="adm-upload-preview">
                <img :src="form.featured_image_preview || imageUrl+form.featured_image" />
                <button type="button" class="adm-upload-remove" @click="removeImg"><X /></button>
              </div>
              <div v-else class="adm-upload" @click="$refs.imgInput.click()"><Upload /><p>Cliquez pour sélectionner une image</p></div>
            </div>

            <div class="adm-field"><label class="adm-label">Description courte</label><textarea v-model="form.description" rows="3" class="adm-input adm-textarea"></textarea></div>
            <div class="adm-field"><label class="adm-label">Description complète</label><textarea v-model="form.full_description" rows="5" class="adm-input adm-textarea"></textarea></div>

            <div class="adm-field">
              <label class="adm-label" style="display:flex;align-items:center;gap:.5rem;cursor:pointer">
                <div class="adm-toggle" :class="{'adm-toggle--on':form.is_featured}" @click="form.is_featured=!form.is_featured"><span class="adm-toggle__knob"></span></div>
                Mettre en avant sur la page d'accueil
              </label>
            </div>
          </form>
          <div class="adm-modal__foot">
            <button class="adm-btn adm-btn--outline" @click="closeDialog">Annuler</button>
            <button class="adm-btn adm-btn--terra" :disabled="saving" @click="submitForm">
              <span v-if="saving" class="adm-ring adm-ring--sm"></span>
              {{ editingEvent ? 'Modifier' : 'Créer' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- ══ MODAL : Inscriptions ══════════════════════ -->
    <Transition name="adm-modal">
      <div v-if="showRegModal" class="adm-modal" @click.self="showRegModal=false">
        <div class="adm-modal__box adm-modal__box--xl" style="max-height:94vh">
          <div class="adm-modal__head">
            <h3 class="adm-modal__title">Inscriptions — {{ selectedEvent?.title }}</h3>
            <button class="adm-modal__close" @click="showRegModal=false"><X /></button>
          </div>
          <div class="adm-modal__body" style="padding:0">
            <!-- Mini KPIs inscriptions -->
            <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:1px;background:var(--ink-12);border-bottom:1px solid var(--ink-12)">
              <div style="background:var(--white);padding:1rem;text-align:center"><p style="font-family:var(--fd);font-size:1.5rem;font-weight:700;color:var(--forest)">{{ regStats.total||0 }}</p><p style="font-size:.75rem;color:var(--ink-60)">Total</p></div>
              <div style="background:var(--white);padding:1rem;text-align:center"><p style="font-family:var(--fd);font-size:1.5rem;font-weight:700;color:var(--green)">{{ regStats.confirmed||0 }}</p><p style="font-size:.75rem;color:var(--ink-60)">Confirmés</p></div>
              <div style="background:var(--white);padding:1rem;text-align:center"><p style="font-family:var(--fd);font-size:1.5rem;font-weight:700;color:var(--amber)">{{ regStats.pending||0 }}</p><p style="font-size:.75rem;color:var(--ink-60)">En attente</p></div>
              <div style="background:var(--white);padding:1rem;text-align:center"><p style="font-family:var(--fd);font-size:1.5rem;font-weight:700;color:var(--red)">{{ regStats.cancelled||0 }}</p><p style="font-size:.75rem;color:var(--ink-60)">Annulés</p></div>
            </div>
            <!-- Filtres inscriptions -->
            <div style="padding:1rem 1.5rem;border-bottom:1px solid var(--ink-12);display:flex;gap:.5rem;flex-wrap:wrap">
              <div class="adm-search" style="flex:1;min-width:180px"><Search /><input v-model="regFilters.search" @input="loadRegistrations" placeholder="Nom, email…" class="adm-input" /></div>
              <select v-model="regFilters.status" @change="loadRegistrations" class="adm-input adm-input--sm">
                <option value="">Tous statuts</option><option value="pending">En attente</option><option value="confirmed">Confirmé</option><option value="cancelled">Annulé</option>
              </select>
            </div>
            <!-- Table inscriptions -->
            <div style="overflow-x:auto;flex:1">
              <div v-if="regLoading" class="adm-loading"><span class="adm-ring"></span></div>
              <table v-else class="adm-table" style="min-width:600px">
                <thead><tr><th>Participant</th><th>Téléphone</th><th>Organisation</th><th>Statut</th><th>Inscrit le</th><th>Actions</th></tr></thead>
                <tbody>
                  <tr v-if="!registrations.length"><td colspan="6" class="adm-table__empty">Aucune inscription</td></tr>
                  <tr v-for="r in registrations" :key="r.id">
                    <td data-label="Participant">
                      <div class="adm-cell-title">{{ r.first_name }} {{ r.last_name }}</div>
                      <div class="adm-cell-sub">{{ r.email }}</div>
                    </td>
                    <td data-label="Téléphone">{{ r.phone||'—' }}</td>
                    <td data-label="Organisation">{{ r.organisation||'—' }}</td>
                    <td data-label="Statut">
                      <span class="adm-badge" :class="{
                        'adm-badge--amber':  r.status==='pending',
                        'adm-badge--green':  r.status==='confirmed',
                        'adm-badge--red':    r.status==='cancelled'
                      }">{{ r.status }}</span>
                    </td>
                    <td data-label="Date" class="adm-cell-date">{{ fmtDate(r.created_at) }}</td>
                    <td class="no-label">
                      <div class="adm-actions">
                        <button v-if="r.status==='pending'" class="adm-btn adm-btn--xs adm-btn--green" @click="confirmReg(r)" title="Confirmer"><Check /></button>
                        <button v-if="r.status!=='cancelled'" class="adm-btn adm-btn--xs adm-btn--red" @click="cancelReg(r)" title="Annuler"><X /></button>
                        <button class="adm-btn adm-btn--xs adm-btn--red" @click="deleteReg(r)" title="Supprimer"><Trash2 /></button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="adm-modal__foot">
            <button class="adm-btn adm-btn--outline" @click="showRegModal=false">Fermer</button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Delete confirm -->
    <Transition name="adm-modal">
      <div v-if="showDeleteConfirm" class="adm-modal" @click.self="showDeleteConfirm=false">
        <div class="adm-modal__box">
          <div class="adm-modal__head"><h3 class="adm-modal__title">Supprimer l'événement ?</h3><button class="adm-modal__close" @click="showDeleteConfirm=false"><X /></button></div>
          <div class="adm-modal__body"><div class="adm-confirm"><div class="adm-confirm__icon"><Trash2 /></div><h3>Confirmer</h3><p>« {{ eventToDelete?.title }} » et toutes ses inscriptions seront définitivement supprimés.</p></div></div>
          <div class="adm-modal__foot">
            <button class="adm-btn adm-btn--outline" @click="showDeleteConfirm=false">Annuler</button>
            <button class="adm-btn adm-btn--red" @click="deleteEventConfirmed"><Trash2 />Supprimer</button>
          </div>
        </div>
      </div>
    </Transition>

    <Transition name="adm-toast"><div v-if="toast.msg" class="adm-toast" :class="{'adm-toast--error':toast.type==='error'}"><CheckCircle />{{ toast.msg }}</div></Transition>
  </main>
</template>

<script>
import axios from 'axios'
import { debounce } from 'lodash'
import { Plus, Search, X, ChevronLeft, ChevronRight, MoreVertical, Eye, EyeOff,
  Edit, Trash2, CheckCircle, Calendar, CalendarDays, Clock, Globe, MapPin,
  Users, Upload, FileText, Check } from 'lucide-vue-next'

const API = () => {
  const token = localStorage.getItem('token')
  return axios.create({
    baseURL: 'https://api.stagfondation.org/api/admin',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
  })
}
const API_FORM = () => {
  const token = localStorage.getItem('token')
  return axios.create({
    baseURL: 'https://api.stagfondation.org/api/admin',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
  })
}

export default {
  name: 'AdminEvents',
  components: { Plus, Search, X, ChevronLeft, ChevronRight, MoreVertical, Eye, EyeOff,
    Edit, Trash2, CheckCircle, Calendar, CalendarDays, Clock, Globe, MapPin,
    Users, Upload, FileText, Check },

  data() {
    return {
      imageUrl: 'https://api.stagfondation.org/',
      loading: false, saving: false, regLoading: false,
      events: [], stats: {}, meta: { current_page:1, last_page:1, total:0 }, displayedPages: [],
      categories: ['Tournoi','Conférence','Formation','Sondage','Gala','Sensibilisation','Événement'],
      filters: { search:'', status:'', filter:'', category:'', page:1 },

      // Formulaire
      showDialog: false, editingEvent: null,
      form: this.emptyForm(),

      // Inscriptions
      showRegModal: false, selectedEvent: null,
      registrations: [], regStats: {}, regMeta: {},
      regFilters: { search:'', status:'' },

      // Suppression
      showDeleteConfirm: false, eventToDelete: null,
      activeMenu: null,
      toast: { msg:'', type:'success' }
    }
  },

  created() {
    this.debounceSearch = debounce(this.loadEvents, 500)
    this.loadEvents()
  },

  methods: {
    emptyForm() {
      return {
        id: null, title:'', category:'Événement', event_type:'public', status:'draft',
        start_date:'', end_date:'', registration_deadline:'',
        city:'Douala', location:'', is_online:false, online_url:'',
        capacity:null, registration_required:true,
        registration_fee:0, fee_currency:'XAF',
        donations_enabled:false, donation_goal:null,
        featured_image:null, featured_image_preview:null,
        description:'', full_description:'', is_featured:false,
      }
    },

    // ── Chargement événements ────────────────────────────────────────────
    async loadEvents() {
      this.loading = true
      try {
        const { data } = await API().get('/events', { params: { ...this.filters } })
        this.events = data.data
        this.meta = data.meta
        this.stats = data.stats || {}
        this.updatePages()
      } catch(e) { this.showToast('Erreur de chargement', 'error') }
      finally { this.loading = false }
    },

    // ── CRUD événements ──────────────────────────────────────────────────
    openNewDialog() { this.editingEvent=null; this.form=this.emptyForm(); this.showDialog=true },
    editEvent(ev) {
      this.editingEvent = ev
      this.form = {
        id: ev.id, title:ev.title, category:ev.category, event_type:ev.event_type,
        status:ev.status, city:ev.city||'', location:ev.location||'',
        is_online:ev.is_online, online_url:ev.online_url||'',
        start_date:ev.start_date?ev.start_date.slice(0,16):'',
        end_date:ev.end_date?ev.end_date.slice(0,16):'',
        registration_deadline:ev.registration_deadline?ev.registration_deadline.slice(0,16):'',
        capacity:ev.capacity, registration_required:ev.registration_required,
        registration_fee:ev.registration_fee||0, fee_currency:ev.fee_currency||'XAF',
        donations_enabled:ev.donations_enabled, donation_goal:ev.donation_goal,
        featured_image:ev.featured_image, featured_image_preview:null,
        description:ev.description||'', full_description:ev.full_description||'',
        is_featured:ev.is_featured
      }
      this.showDialog=true; this.activeMenu=null
    },
    closeDialog() { this.showDialog=false; this.editingEvent=null; this.form=this.emptyForm() },

    handleImg(e) {
      const f = e.target.files[0]
      if (f) { this.form.featured_image=f; this.form.featured_image_preview=URL.createObjectURL(f) }
    },
    removeImg() { this.form.featured_image=null; this.form.featured_image_preview=null; if(this.$refs.imgInput)this.$refs.imgInput.value='' },

    async submitForm() {
      this.saving = true
      try {
        const fd = new FormData()
        const fields = ['title','category','event_type','status','city','location','online_url',
          'start_date','end_date','registration_deadline','description','full_description',
          'registration_fee','fee_currency','donation_goal']
        fields.forEach(k => { if(this.form[k]!==null&&this.form[k]!==undefined)fd.append(k,this.form[k]) })
        if(this.form.id) fd.append('id', this.form.id)
        if(this.form.capacity) fd.append('capacity', this.form.capacity)
        fd.append('is_online', this.form.is_online?'1':'0')
        fd.append('registration_required', this.form.registration_required?'1':'0')
        fd.append('donations_enabled', this.form.donations_enabled?'1':'0')
        fd.append('is_featured', this.form.is_featured?'1':'0')
        if(this.form.featured_image instanceof File) fd.append('featured_image', this.form.featured_image)
        await API_FORM().post('/events', fd)
        this.showToast(this.form.id?'Événement modifié':'Événement créé')
        this.closeDialog(); this.loadEvents()
      } catch(e) { this.showToast(e.response?.data?.message||'Erreur lors de la sauvegarde','error') }
      finally { this.saving=false }
    },

    async toggleStatus(ev) {
      const next = ev.status==='published' ? 'draft' : 'published'
      try { await API().patch(`/events/${ev.id}/toggle-status`, { status: next }); this.loadEvents() }
      catch(e) { this.showToast('Erreur','error') }
      this.activeMenu=null
    },

    confirmDelete(ev) { this.eventToDelete=ev; this.showDeleteConfirm=true; this.activeMenu=null },
    async deleteEventConfirmed() {
      try { await API().delete(`/events/${this.eventToDelete.id}`); this.showToast('Événement supprimé'); this.showDeleteConfirm=false; this.eventToDelete=null; this.loadEvents() }
      catch(e) { this.showToast('Erreur lors de la suppression','error') }
    },

    // ── Inscriptions ─────────────────────────────────────────────────────
    async openRegistrations(ev) {
      this.selectedEvent=ev; this.registrations=[]; this.regStats={}; this.showRegModal=true
      await this.loadRegistrations()
    },
    async loadRegistrations() {
      if(!this.selectedEvent) return
      this.regLoading=true
      try {
        const { data } = await API().get(`/events/${this.selectedEvent.id}/registrations`, { params: this.regFilters })
        this.registrations=data.data; this.regStats=data.stats||{}
      } catch(e) { this.showToast('Erreur de chargement des inscriptions','error') }
      finally { this.regLoading=false }
    },
    async confirmReg(r) { try { await API().patch(`/events/registrations/${r.id}/confirm`); this.showToast('Inscription confirmée'); this.loadRegistrations() } catch(e) { this.showToast('Erreur','error') } },
    async cancelReg(r) { try { await API().patch(`/events/registrations/${r.id}/cancel`); this.showToast('Inscription annulée'); this.loadRegistrations() } catch(e) { this.showToast('Erreur','error') } },
    async deleteReg(r) { if(!confirm('Supprimer cette inscription ?')) return; try { await API().delete(`/events/registrations/${r.id}`); this.showToast('Supprimé'); this.loadRegistrations() } catch(e) { this.showToast('Erreur','error') } },

    // ── Utilitaires ──────────────────────────────────────────────────────
    resetFilters() { this.filters={search:'',status:'',filter:'',category:'',page:1}; this.loadEvents() },
    changePage(p) { if(p>=1&&p<=this.meta.last_page){this.filters.page=p;this.loadEvents()} },
    updatePages() {
      const c=this.meta.current_page, l=this.meta.last_page, d=2, pages=[]
      for(let i=1;i<=l;i++) if(i===1||i===l||(i>=c-d&&i<=c+d)) pages.push(i)
      this.displayedPages=pages
    },
    toggleMenu(id) { this.activeMenu=this.activeMenu===id?null:id },
    fmtDate(d) { if(!d)return'—'; return new Date(d).toLocaleDateString('fr-FR',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}).replace(',','') },
    fmtAmount(v) { return v?`${Number(v).toLocaleString('fr-FR')} €`:'0 €' },
    statusLabel(s) { return{draft:'Brouillon',published:'Publié',cancelled:'Annulé',completed:'Terminé'}[s]||s },
    showToast(msg, type='success') { this.toast={msg,type}; setTimeout(()=>{this.toast={msg:'',type:'success'}},4000) }
  },

  mounted() { document.addEventListener('click', e=>{ if(!e.target.closest('.adm-drop')) this.activeMenu=null }) }
}
</script>
<style>@import './admin.css';</style>